// Port of ios/Sources/Calls/CallManager.swift — the one owner of a call, at PAGE scope (it outlives
// any screen). It drives CallSignalingSocket (the session), CallRtcSession (the media) and the
// tones, and folds everything into one `CallUiState` the UI reads through `useCallState()`.
// Exactly one call at a time — the plugin itself rejects a second join.
//
// Everything that is NOT media reaches the manager through a `CallBackend`: in the web app that is
// the worker (callManager.ts — it owns REST and the main socket, data/features/calls.ts, and hands
// over the active-call table, the plugin config, the decline); in the Windows app's hidden call host
// (src/callhost/) it is the native app over the WebView2 message bridge. This file imports neither,
// so the host bundle carries no worker. Everything media-shaped is here, because only a page has
// RTCPeerConnection, getUserMedia and a speaker.
import type { CallPerson, CallsConfig, CallsView } from '../../../data/features/calls';
import { incomingCallToRing, ringKeepsGoing, RING_CHECK_MS } from '../../../core/activeCalls';
import { CallCameraChoice, type CallCamera } from '../../../core/callCameraChoice';
import { CallAudioOutputChoice, type CallAudioOutput } from '../../../core/callAudioOutput';
import { CallRoster, handRaised, type CallParticipant, type Roster } from '../../../core/callRoster';
import { CallEmoji, type CallSocketEvent } from '../../../core/callsCodec';
import { CallScreenPolicy, type CallPhase } from '../../../core/callScreenPolicy';
import { ScreenAwake } from '../../../core/screenAwake';
import { screenAwake } from '../../screenAwake';
import { CallTones } from '../../../core/callTones';
import { CallJoinWatchdog } from '../../../core/callJoinWatchdog';
import { hasNoAudioInput, joinsWith, micFailure, type MicReadiness } from '../../../core/callMicrophone';
import { t, tf } from '../../../i18n/i18n';
import { CallRtcSession } from './callRtcSession';
import { CallSignalingSocket } from './callSignalingSocket';
import { playChime, TonePlayer } from './tones';

/** What the manager needs from outside the page's media: the active-call table, REST, the token. */
export interface CallBackend {
  /** Subscribes to the active-call table's changes (the web: the worker's `calls` signal). */
  onActiveCalls(listener: (view: CallsView) => void): void;
  callsState(): Promise<CallsView>;
  /** Re-reads the server's table now — after this device's own call ends. */
  refreshCalls(): Promise<CallsView>;
  /** STUN/TURN + EnableVideo. A failure must resolve to no TURN, no video — never reject the join. */
  callsConfig(): Promise<CallsConfig>;
  dismissCallNotification(channelId: string): Promise<unknown>;
  /** Faces for a roster the table has not seen yet. */
  callPeople(userIds: string[]): Promise<Record<string, CallPerson>>;
  /** The call socket's bearer token; null = the page's own session cookie authenticates it. */
  token(): string | null;
  /** A call takes the microphone and the speaker: voice notes pause / stop. */
  interruptMedia(): void;
  /** A face for the ring's notification, or undefined. */
  avatarUrl(userId: string): string | undefined;
  /** A join that never reached media, with the call's log (core/callJoinWatchdog.ts). The web app sends
   *  it to Core; the Windows host leaves it out — the native app reports from the log lines it relays. */
  reportCallFailure?(description: string, log: string[]): void;
}

export interface IncomingCall {
  channelId: string;
  callId: string;
  callerId: string;
  callerName: string;
  /** An invitation into a call already running (shared/API.md §30.3): the call's conversation title. */
  inviteTitle?: string;
}

/** `noMicrophone`: joined LISTENING ONLY (no input device — not a refusal); `micUnavailable`: there is a
 *  microphone but it could not be opened (another app holds it, or the OS blocks it) — core/callMicrophone.ts. */
export type CallNotice = 'micDenied' | 'screenDenied' | 'callFailed' | 'callEnded' | 'alreadyInCall' | 'cameraDenied' | 'noMicrophone' | 'micUnavailable';

/** One emoji reaction floating over the call — a moment, not state. */
export interface CallReactionBurst {
  id: number;
  userId: string;
  sessionId: string;
  glyph: string;
  at: number;
}

export interface CallUiState {
  phase: CallPhase;
  channelId: string;
  channelName: string;
  startAt: number;
  muted: boolean;
  /** This device has no microphone: the call is listen-only until one appears (unmute tries again). */
  micMissing: boolean;
  participants: CallParticipant[];
  people: Record<string, CallPerson>;
  sharingScreen: boolean;
  remoteScreenSharing: boolean;
  incoming: IncomingCall | null;
  notice: CallNotice | null;
  handRaised: boolean;
  reactions: CallReactionBurst[];
  sessionId: string;
  videoAllowed: boolean;
  cameraOn: boolean;
  cameras: CallCamera[];
  cameraId: string | null;
  /** Where the voices come out (P145): the browser's outputs when it can route (`setSinkId`). */
  audioOutputs: CallAudioOutput[];
  audioOutputId: string;
  /** The expanded huddle (the Mac's huddle WINDOW, here an in-page panel). */
  expanded: boolean;
  remoteScreen: MediaStream | null;
  remoteVideos: Record<string, MediaStream>;
  localVideo: MediaStream | null;
  /** The last few lifecycle events, timestamped (the Mac's diagnostics panel). */
  log: string[];
}

const idle = (): CallUiState => ({
  phase: 'idle',
  channelId: '',
  channelName: '',
  startAt: 0,
  muted: true,
  micMissing: false,
  participants: [],
  people: {},
  sharingScreen: false,
  remoteScreenSharing: false,
  incoming: null,
  notice: null,
  handRaised: false,
  reactions: [],
  sessionId: '',
  videoAllowed: false,
  cameraOn: false,
  cameras: [],
  cameraId: null,
  audioOutputs: [],
  audioOutputId: CallAudioOutputChoice.defaultId,
  expanded: false,
  remoteScreen: null,
  remoteVideos: {},
  localVideo: null,
  log: [],
});

export const NOTICE_KEYS: Record<CallNotice, string> = {
  micDenied: 'call.notice.micDenied',
  screenDenied: 'call.notice.screenDenied',
  callFailed: 'call.notice.failed',
  callEnded: 'call.notice.ended',
  alreadyInCall: 'call.notice.already',
  cameraDenied: 'call.notice.cameraDenied',
  noMicrophone: 'web.call.notice.noMicrophone',
  micUnavailable: 'web.call.notice.micUnavailable',
};

const REACTION_LIFETIME_MS = 4_000;
const INCOMING_TAG = 'satori.incoming-call';

type Listener = () => void;

export class CallManager {
  static readonly reactionLifetimeMs = REACTION_LIFETIME_MS;

  private state: CallUiState = idle();
  private listeners = new Set<Listener>();
  private activeView: CallsView = { calls: [], channelTypes: {}, people: {}, unavailable: false };
  private activeListeners = new Set<Listener>();

  private me: string | null = null;
  private serverBase = '';
  private socket: CallSignalingSocket | null = null;
  private joinStallTimer: ReturnType<typeof setTimeout> | null = null;
  /** Which try of this join is running (CallJoinWatchdog): 1, or 2 after a stall. */
  private joinAttempt = 0;
  private rtc: CallRtcSession | null = null;
  private roster: Roster = {};
  private iceServers: CallsConfig['iceServers'] = [];
  private waitingTone = new TonePlayer();
  private ringer = new TonePlayer();
  private sawPeerThisCall = false;
  private lastChimeAt = 0;
  private ringCheck: ReturnType<typeof setInterval> | null = null;
  private ringStartedAt = 0;
  private knownCallIds = new Set<string>();
  /** Channels whose call must NOT ring here until the deadline: a call this device followed into (§30.3 move). */
  private suppressedRings = new Map<string, number>();
  /** A moved call keeps the expanded huddle open across the leave + join. */
  private expandOnJoin = false;
  private incomingNotification: Notification | null = null;
  private audioElements = new Map<string, HTMLAudioElement>();
  private reactionSeq = 0;
  private screenStream: MediaStream | null = null;
  private deviceListener: (() => void) | null = null;
  private wired = false;

  constructor(private readonly backend: CallBackend) {}

  // MARK: - Store plumbing (useSyncExternalStore)

  getState = (): CallUiState => this.state;
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  getActive = (): CallsView => this.activeView;
  subscribeActive = (listener: Listener): (() => void) => {
    this.activeListeners.add(listener);
    return () => this.activeListeners.delete(listener);
  };

  private set(patch: Partial<CallUiState>): void {
    const before = this.state;
    this.state = { ...before, ...patch };
    if (before.phase !== this.state.phase) this.applyScreenPolicy();
    for (const l of this.listeners) l();
  }

  /** Called once by CallHost: the worker's signals, page unload, and who "me" is. */
  wire(me: string | null, serverBase: string): void {
    this.me = me;
    this.serverBase = serverBase;
    if (this.wired) return;
    this.wired = true;
    this.backend.onActiveCalls((view) => this.onActiveCallsChanged(view));
    void this.backend
      .callsState()
      .then((view) => this.onActiveCallsChanged(view, true))
      .catch(() => undefined);
    // Leaving cleanly when the tab closes: the server drops the session at once instead of after
    // its reconnect window, so nobody sees a ghost on the call for 20 s.
    window.addEventListener('pagehide', () => {
      this.socket?.leave();
      this.rtc?.close();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.applyScreenPolicy();
    });
    if (localStorage.getItem('satori.callDebug') === '1') {
      (globalThis as unknown as { __satoriCall: unknown }).__satoriCall = this;
    }
  }

  /** Who "me" is (the invite feature's author checks). */
  get userId(): string | null {
    return this.me;
  }

  /** Debug/verification only: the live peer connection and the remote audio elements. */
  get debug(): { pc: RTCPeerConnection | null; audio: HTMLAudioElement[] } {
    return { pc: this.rtc?.pc ?? null, audio: [...this.audioElements.values()] };
  }

  logCallEvent(message: string): void {
    const time = new Date().toTimeString().slice(0, 8);
    this.set({ log: [...this.state.log, `${time} ${message}`].slice(-12) });
  }

  // MARK: - Joining and leaving

  join(channelId: string, channelName: string): void {
    if (this.state.phase !== 'idle') {
      if (this.state.channelId !== channelId) this.set({ notice: 'alreadyInCall' });
      return;
    }
    this.stopRinging();
    void (async () => {
      const mic = await this.microphoneReadiness();
      if (!joinsWith(mic)) {
        this.logCallEvent(`join refused: microphone ${mic}`);
        this.set({ notice: mic === 'denied' ? 'micDenied' : 'micUnavailable', incoming: null });
        return;
      }
      // No microphone at all is not a refusal: join and listen (Mattermost's own client does the same).
      await this.doJoin(channelId, channelName, mic === 'missing');
    })();
  }

  private async doJoin(channelId: string, channelName: string, micMissing = false): Promise<void> {
    // A call takes the microphone and the speaker: a voice note being recorded PAUSES (the voice
    // feature listens for this event — its recorder.ts), one being played stops.
    this.backend.interruptMedia();
    this.roster = {};
    this.sawPeerThisCall = false;
    this.state = {
      ...idle(),
      phase: 'joining',
      channelId,
      channelName,
      startAt: Date.now(),
      people: this.state.people,
      micMissing,
      notice: micMissing ? 'noMicrophone' : null,
      expanded: this.expandOnJoin,
    };
    this.expandOnJoin = false;
    if (micMissing) this.logCallEvent('join listen-only: no microphone');
    this.applyScreenPolicy();
    this.emit();
    // TURN/STUN before the socket; a failed fetch degrades to host candidates.
    const config = await this.backend.callsConfig().catch(() => ({ iceServers: [], videoEnabled: false }));
    this.iceServers = config.iceServers;
    if (this.state.phase !== 'joining' || this.state.channelId !== channelId) return;
    this.set({ videoAllowed: config.videoEnabled });
    if (config.videoEnabled) void this.refreshCameras();
    // Outputs (P145) whatever the video setting; the hotplug watch serves both.
    void this.refreshAudioOutputs();
    this.observeCameraHotplug();
    this.joinAttempt = 1;
    this.openSignaling(channelId);
  }

  /** The call socket (and, once it is admitted, the peer connection) for `channelId`, under the join watchdog. */
  private openSignaling(channelId: string): void {
    this.armJoinWatchdog(channelId);
    const socket: CallSignalingSocket = new CallSignalingSocket(
      () => (this.serverBase || location.origin).replace(/^http/, 'ws') + '/api/v4/websocket',
      () => this.backend.token(),
      {
        // A socket the join watchdog replaced is deaf: its late frames must not reach the new attempt.
        joined: () => this.socket === socket && this.onSocketJoined(),
        signal: (json) => this.socket === socket && this.rtc?.handleRemoteSignal(json),
        callState: (json) => this.socket === socket && this.onCallState(json),
        rosterEvent: (event) => this.socket === socket && this.onRosterEvent(event),
        closed: (reason) => {
          if (this.socket !== socket) return;
          this.logCallEvent(`socket closed reason=${reason ?? '-'} phase=${this.state.phase}`);
          if (this.state.phase !== 'idle') this.teardown('callFailed');
        },
        log: (line) => this.logCallEvent(line),
      },
    );
    this.socket = socket;
    socket.connect(channelId);
  }

  leave(): void {
    this.teardown(null);
  }

  // MARK: - The join watchdog (core/callJoinWatchdog.ts)

  private armJoinWatchdog(channelId: string): void {
    this.clearJoinWatchdog();
    const attempt = this.joinAttempt;
    this.joinStallTimer = setTimeout(() => this.onJoinStalled(channelId, attempt), CallJoinWatchdog.stallMillis);
  }

  private clearJoinWatchdog(): void {
    if (this.joinStallTimer !== null) clearTimeout(this.joinStallTimer);
    this.joinStallTimer = null;
  }

  private onJoinStalled(channelId: string, attempt: number): void {
    this.joinStallTimer = null;
    if (this.state.phase !== 'joining' || this.state.channelId !== channelId || this.joinAttempt !== attempt) return;
    const stage = this.state.log.at(-1)?.slice(9) ?? '-';
    if (CallJoinWatchdog.decide(attempt) === 'retry') {
      this.logCallEvent(`join stalled (attempt ${attempt}, last: ${stage}): retrying on a fresh socket`);
      // Only the transport goes: the phase stays «joining», so nothing outside the page sees an end.
      this.socket?.leave();
      this.socket = null;
      this.rtc?.close();
      this.rtc = null;
      this.joinAttempt = attempt + 1;
      this.openSignaling(channelId);
      return;
    }
    const description = CallJoinWatchdog.describe(attempt, stage);
    this.logCallEvent(description);
    try {
      this.backend.reportCallFailure?.(description, this.state.log);
    } catch {
      /* reporting must never be the second failure */
    }
    this.teardown('callFailed');
  }

  acceptIncoming(): void {
    const incoming = this.state.incoming;
    if (!incoming) return;
    this.stopRinging();
    this.set({ incoming: null });
    // An invitation can ring while this device is on ANOTHER call (§30.3): answering it is
    // "end and accept", the way a phone does it — never a refusal with «Ya estás en una llamada».
    if (this.state.phase !== 'idle' && this.state.channelId !== incoming.channelId) this.teardown(null);
    this.join(incoming.channelId, incoming.inviteTitle || incoming.callerName);
  }

  /**
   * Rings for an invitation (§30.3 `CallInviteSignal.shouldRing` already said yes): the same card,
   * loop, notification and 45 s timeout as a call that starts. One ring at a time; the same call
   * ringing already is not rung twice.
   */
  ringInvite(incoming: IncomingCall): void {
    if (this.state.incoming) return;
    if (this.state.phase !== 'idle' && this.state.channelId === incoming.channelId) return;
    this.logCallEvent(`[callInvite] ring call=${incoming.callId} from=${incoming.callerId}`);
    this.set({ incoming });
    this.startRinging();
    this.raiseIncomingAlert(incoming.callerName, incoming.callerId);
  }

  /**
   * Moves this device's call to `channelId` (§30.3): leave the current call, join the one there
   * (joining a channel with no call starts it). That channel's call never rings here for the next
   * 90 s — this device is on its way in, or already there.
   */
  moveTo(channelId: string, channelName: string): void {
    this.suppressedRings.set(channelId, Date.now() + 90_000);
    if (this.state.incoming?.channelId === channelId) {
      this.stopRinging();
      this.set({ incoming: null });
    }
    if (this.state.phase !== 'idle' && this.state.channelId === channelId) return;
    this.logCallEvent(`[callInvite] move from=${this.state.channelId || '-'} to=${channelId}`);
    this.expandOnJoin = this.state.expanded;
    if (this.state.phase !== 'idle') this.teardown(null);
    this.join(channelId, channelName);
  }

  declineIncoming(): void {
    const incoming = this.state.incoming;
    if (!incoming) return;
    this.stopRinging();
    this.set({ incoming: null });
    void this.backend.dismissCallNotification(incoming.channelId).catch(() => undefined);
  }

  consumeNotice(): void {
    if (this.state.notice) this.set({ notice: null });
  }

  setExpanded(expanded: boolean): void {
    if (this.state.expanded !== expanded) this.set({ expanded });
  }

  private teardown(notice: CallNotice | null): void {
    this.logCallEvent(`teardown notice=${notice ?? '-'} roster=${Object.keys(this.roster).length}`);
    this.clearJoinWatchdog();
    this.sawPeerThisCall = false;
    this.waitingTone.stop();
    this.stopDeviceObservation();
    this.socket?.leave();
    this.socket = null;
    this.rtc?.close();
    this.rtc = null;
    this.screenStream?.getTracks().forEach((track) => track.stop());
    this.screenStream = null;
    this.state.localVideo?.getTracks().forEach((track) => track.stop());
    for (const audio of this.audioElements.values()) {
      audio.srcObject = null;
      audio.remove();
    }
    this.audioElements.clear();
    this.roster = {};
    const keep = { incoming: this.state.incoming, people: this.state.people, log: this.state.log };
    this.state = { ...idle(), ...keep, notice };
    this.applyScreenPolicy();
    this.emit();
    // An immediate hang-up can outrun the join's own broadcasts: re-read the server's table.
    void this.backend
      .refreshCalls()
      .then((view) => this.onActiveCallsChanged(view))
      .catch(() => undefined);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  // MARK: - In-call controls

  toggleMute(): void {
    if (this.state.phase !== 'active' || !this.rtc) return;
    if (!this.state.muted) {
      this.applyMute(true);
      return;
    }
    // Unmuting is the first moment a call OPENS the microphone (joining only plays the other side).
    void (async () => {
      const rtc = this.rtc;
      if (!rtc) return;
      let fresh: MediaStreamTrack | undefined;
      if (!rtc.hasLiveMic) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
          fresh = stream.getAudioTracks()[0];
        } catch (error) {
          this.logCallEvent(`unmute refused: ${String(error)}`);
          const failure = micFailure(error);
          if (failure === 'missing') this.set({ notice: 'noMicrophone', micMissing: true });
          else this.set({ notice: failure === 'denied' ? 'micDenied' : 'micUnavailable' });
          return;
        }
      }
      if (this.rtc !== rtc || this.state.phase !== 'active') {
        fresh?.stop();
        return;
      }
      if (this.state.micMissing) this.set({ micMissing: false });
      this.applyMute(false, fresh);
    })();
  }

  private applyMute(muted: boolean, fresh?: MediaStreamTrack): void {
    this.logCallEvent(muted ? 'mute' : 'unmute');
    void this.rtc?.setMuted(muted, fresh);
    this.socket?.sendMute(muted);
    this.set({ muted });
  }

  /** Granted/denied answered from the current state; only "prompt" actually asks — at JOIN, the
   *  apps' moment. The capture is released at once: a muted call holds no microphone. A device with
   *  NO microphone answers 'missing' (listen-only), never "denied" (core/callMicrophone.ts). */
  private async microphoneReadiness(): Promise<MicReadiness> {
    // No getUserMedia at all = not a secure context: nothing the person can grant.
    if (!navigator.mediaDevices?.getUserMedia) return 'unavailable';
    // Counting inputs needs no permission (labels do): no microphone → never ask for one.
    const devices = await navigator.mediaDevices.enumerateDevices?.().catch(() => null);
    if (devices && hasNoAudioInput(devices)) return 'missing';
    try {
      const status = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
      if (status?.state === 'granted') return 'granted';
      if (status?.state === 'denied') return 'denied';
    } catch {
      /* Firefox has no 'microphone' permission name: ask */
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return 'granted';
    } catch (error) {
      this.logCallEvent(`microphone: ${String(error)}`);
      return micFailure(error);
    }
  }

  /** Re-reads the cameras and settles which one is "the" camera (CallCameraChoice). */
  async refreshCameras(): Promise<void> {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices().catch(() => [] as MediaDeviceInfo[]);
    const cameras: CallCamera[] = devices
      .filter((d) => d.kind === 'videoinput' && d.deviceId)
      .map((d, i) => ({ id: d.deviceId, name: d.label || `${t('call.camera.short')} ${i + 1}`, position: CallCameraChoice.positionOf(undefined, d.label) }));
    let saved: string | null = this.state.cameraId;
    if (!saved) {
      try {
        saved = localStorage.getItem(CallCameraChoice.storageKey);
      } catch {
        saved = null;
      }
    }
    const preferFront = matchMedia('(pointer: coarse)').matches;
    const chosen = CallCameraChoice.resolve(cameras, saved, preferFront);
    const lost = this.state.cameraOn && this.state.cameraId !== null && cameras.length > 0 && chosen?.id !== this.state.cameraId;
    this.set({ cameras, cameraId: chosen?.id ?? null });
    // The camera in use was unplugged: carry on with the next one rather than freeze.
    if (lost && chosen) void this.switchRunningCamera(chosen.id);
  }

  /** Re-reads the audio outputs (labels exist once the microphone was granted) and settles the route. */
  async refreshAudioOutputs(): Promise<void> {
    if (!canRouteAudio() || !navigator.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices().catch(() => [] as MediaDeviceInfo[]);
    const outputs = CallAudioOutputChoice.fromDevices(devices, t('call.route.speaker'));
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(CallAudioOutputChoice.storageKey);
    } catch {
      saved = null;
    }
    const id = CallAudioOutputChoice.resolve(outputs, saved);
    const changed = id !== this.state.audioOutputId;
    this.set({ audioOutputs: outputs, audioOutputId: id });
    // The chosen output was unplugged: the voices follow the browser default instead of going silent.
    if (changed) this.applyAudioOutput();
  }

  /** The route picker: every voice moves at once; remembered for the next call. */
  selectAudioOutput(id: string): void {
    try {
      if (id === CallAudioOutputChoice.defaultId) localStorage.removeItem(CallAudioOutputChoice.storageKey);
      else localStorage.setItem(CallAudioOutputChoice.storageKey, id);
    } catch {
      /* a per-viewer convenience */
    }
    this.set({ audioOutputId: id });
    this.applyAudioOutput();
  }

  private applyAudioOutput(audio?: HTMLAudioElement): void {
    if (!canRouteAudio()) return;
    const sink = this.state.audioOutputId === CallAudioOutputChoice.defaultId ? '' : this.state.audioOutputId;
    for (const el of audio ? [audio] : this.audioElements.values()) {
      const media = el as HTMLAudioElement & { setSinkId?(id: string): Promise<void> };
      void media.setSinkId?.(sink).catch((error) => this.logCallEvent(`audio output: ${String(error)}`));
    }
  }

  private observeCameraHotplug(): void {
    if (this.deviceListener || !navigator.mediaDevices?.addEventListener) return;
    this.deviceListener = () => {
      if (this.state.phase !== 'idle') {
        void this.refreshCameras();
        void this.refreshAudioOutputs();
      }
    };
    navigator.mediaDevices.addEventListener('devicechange', this.deviceListener);
  }

  private stopDeviceObservation(): void {
    if (this.deviceListener) navigator.mediaDevices?.removeEventListener('devicechange', this.deviceListener);
    this.deviceListener = null;
  }

  private async openCamera(deviceId: string | null): Promise<MediaStreamTrack | null> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // A call-sized format (~640 px, 24 fps): every pixel is upload bandwidth and SFU CPU.
        video: { deviceId: deviceId ? { exact: deviceId } : undefined, width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 24, max: 24 } },
      });
      return stream.getVideoTracks()[0] ?? null;
    } catch {
      return null;
    }
  }

  /** The camera picker: switches a running camera live, or turns the camera on. Remembered. */
  selectCamera(id: string): void {
    try {
      localStorage.setItem(CallCameraChoice.storageKey, id);
    } catch {
      /* a per-viewer convenience */
    }
    this.set({ cameraId: id });
    if (this.state.cameraOn) void this.switchRunningCamera(id);
    else this.toggleCamera();
  }

  private async switchRunningCamera(id: string): Promise<void> {
    const track = await this.openCamera(id);
    if (!track || !this.rtc || !this.state.cameraOn) {
      track?.stop();
      if (!track) this.set({ notice: 'cameraDenied' });
      return;
    }
    const ok = await this.rtc.switchCamera(track);
    if (!ok) {
      track.stop();
      return;
    }
    this.set({ localVideo: new MediaStream([track]) });
  }

  /** `video_on` announces the stream id BEFORE the track (rtcd drops an unannounced video track). */
  toggleCamera(): void {
    const rtc = this.rtc;
    const socket = this.socket;
    if (this.state.phase !== 'active' || !this.state.videoAllowed || !rtc || !socket) return;
    if (this.state.cameraOn) {
      void rtc.stopCamera();
      socket.sendVideoOff();
      this.state.localVideo?.getTracks().forEach((track) => track.stop());
      this.set({ cameraOn: false, localVideo: null });
      return;
    }
    void (async () => {
      const track = await this.openCamera(this.state.cameraId);
      if (!track) {
        this.set({ notice: 'cameraDenied' });
        return;
      }
      if (this.rtc !== rtc || this.state.phase !== 'active' || this.state.cameraOn) {
        track.stop();
        return;
      }
      await this.refreshCameras(); // labels exist only once a camera was granted
      const settings = track.getSettings();
      const stream = new MediaStream([track]);
      socket.sendVideoOn(stream.id);
      this.set({ cameraOn: true, localVideo: stream, cameraId: settings.deviceId ?? this.state.cameraId });
      await rtc.startCamera(stream);
    })();
  }

  /** The self-view mirrors only a camera that faces the person. */
  get selfViewMirrored(): boolean {
    const camera = this.state.cameras.find((c) => c.id === this.state.cameraId) ?? null;
    const facing = this.state.localVideo?.getVideoTracks()[0]?.getSettings().facingMode;
    if (facing === 'environment') return false;
    return CallCameraChoice.mirrorsSelfView(camera);
  }

  /** ✋ — optimistic; the server's echo for this session confirms or corrects it. */
  toggleRaiseHand(): void {
    if (this.state.phase !== 'active') return;
    const raise = !this.state.handRaised;
    this.logCallEvent(raise ? 'raise hand' : 'lower hand');
    this.set({ handRaised: raise });
    this.socket?.sendRaiseHand(raise);
  }

  /** Shown here at once; the server's echo for this same session is then skipped. */
  sendReaction(name: string): void {
    const emoji = CallEmoji.fromName(name);
    if (this.state.phase !== 'active' || !emoji) return;
    this.socket?.sendReaction(emoji);
    this.addReaction(this.me ?? '', this.state.sessionId, CallEmoji.glyph(emoji));
  }

  private addReaction(userId: string, sessionId: string, glyph: string): void {
    const burst: CallReactionBurst = { id: ++this.reactionSeq, userId, sessionId, glyph, at: Date.now() };
    // A handful at once is a celebration; dozens is noise and a layout cost.
    this.set({ reactions: [...this.state.reactions, burst].slice(-12) });
    setTimeout(() => this.set({ reactions: this.state.reactions.filter((r) => r.id !== burst.id) }), REACTION_LIFETIME_MS);
  }

  /** The join/leave chime, for OTHER people only and never more than one per 0.6 s. */
  private chimeIfNeeded(event: CallSocketEvent): void {
    if (this.state.phase !== 'active') return;
    let kind: 'join' | 'leave';
    if (event.kind === 'userJoined') {
      if (event.sessionId === this.state.sessionId || this.roster[event.sessionId]) return;
      kind = 'join';
    } else if (event.kind === 'userLeft') {
      if (event.sessionId === this.state.sessionId || !this.roster[event.sessionId]) return;
      kind = 'leave';
    } else return;
    const now = Date.now();
    if (now - this.lastChimeAt <= CallTones.chimeGapMillis) return;
    this.lastChimeAt = now;
    playChime(kind);
  }

  // MARK: - Screen share (getDisplayMedia: the browser's own picker is the confirmation)

  requestScreenShare(): void {
    const rtc = this.rtc;
    const socket = this.socket;
    if (this.state.phase !== 'active' || this.state.sharingScreen || !rtc || !socket) return;
    void (async () => {
      let stream: MediaStream;
      try {
        // The picker offers everything there is to share — an entire screen, one window, a tab
        // (user order 2026-10-13: "ask which screen or application or all the screen") — minus
        // this very tab, which would only mirror the call back into itself. Chromium reads the
        // hints; other browsers ignore them and show their own full picker.
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: { ideal: 15, max: 30 } },
          audio: false,
          monitorTypeSurfaces: 'include',
          selfBrowserSurface: 'exclude',
          surfaceSwitching: 'include',
        } as DisplayMediaStreamOptions);
      } catch (error) {
        // Named in the call log: a refusal (NotAllowedError: the reader cancelled, or no user activation)
        // reads very differently from a capture that could not start (NotReadableError / AbortError).
        const e = error as { name?: string; message?: string };
        this.logCallEvent(`screen share refused: ${e?.name ?? 'Error'} ${e?.message ?? ''}`.trim());
        this.set({ notice: 'screenDenied' });
        return;
      }
      if (this.rtc !== rtc || this.state.phase !== 'active' || this.state.sharingScreen) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const track = stream.getVideoTracks()[0];
      // What the reader chose (monitor / window / browser) and its size: the picker's only trace.
      const settings = (track?.getSettings?.() ?? {}) as MediaTrackSettings & { displaySurface?: string };
      this.logCallEvent(`screen share: ${settings.displaySurface ?? '?'} ${settings.width ?? '?'}x${settings.height ?? '?'} "${track?.label ?? ''}"`);
      // The browser's own "Stop sharing" bar ends the track: that is a stop like ours.
      track?.addEventListener('ended', () => this.stopScreenShare());
      this.screenStream = stream;
      socket.sendScreenOn(stream.id);
      this.set({ sharingScreen: true });
      await rtc.startScreenShare(stream);
    })();
  }

  stopScreenShare(): void {
    const stream = this.screenStream;
    this.screenStream = null;
    stream?.getTracks().forEach((track) => track.stop());
    if (!this.state.sharingScreen) return;
    void this.rtc?.stopScreenShare();
    this.socket?.sendScreenOff();
    this.set({ sharingScreen: false });
  }

  // MARK: - Incoming calls (ringing)

  /** Fed the active-call table every time it changes: NEW call in a DM/GM by someone else → ring. */
  private onActiveCallsChanged(view: CallsView, initial = false): void {
    this.activeView = view;
    for (const l of this.activeListeners) l();
    const previous = this.knownCallIds;
    this.knownCallIds = new Set(view.calls.map((c) => c.id));
    // The FIRST table the page sees is history, not news: a call already running when the tab
    // opened is shown by the banner and the row marker, never rung for.
    if (initial && previous.size === 0) return;

    const incoming = this.state.incoming;
    if (incoming && !view.calls.some((c) => c.id === incoming.callId)) {
      this.stopRinging();
      this.set({ incoming: null });
      return;
    }
    if (incoming && !this.ringStillWanted()) {
      this.stopRinging();
      this.set({ incoming: null });
      return;
    }
    const fresh = incomingCallToRing({
      calls: view.calls,
      previousCallIds: previous,
      currentUserId: this.me,
      idle: this.state.phase === 'idle',
      alreadyRinging: !!this.state.incoming,
      channelType: (id) => view.channelTypes[id] ?? null,
    });
    if (!fresh) return;
    const suppressedUntil = this.suppressedRings.get(fresh.channelId) ?? 0;
    if (suppressedUntil > Date.now()) {
      this.logCallEvent(`[callInvite] ring suppressed (moved) call=${fresh.id}`);
      return;
    }
    const callerName = view.people[fresh.ownerId]?.name ?? '';
    this.set({ incoming: { channelId: fresh.channelId, callId: fresh.id, callerId: fresh.ownerId, callerName } });
    this.startRinging();
    this.raiseIncomingAlert(callerName, fresh.ownerId);
  }

  private startRinging(): void {
    this.stopRinging(false);
    this.ringer.start('ring');
    this.ringStartedAt = Date.now();
    // No fixed cut-off: every check re-reads the server's table (the socket may be dead) and the
    // ring goes on while the caller is still waiting alone (ringKeepsGoing).
    this.ringCheck = setInterval(() => {
      if (!this.ringStillWanted()) {
        this.stopRinging();
        this.set({ incoming: null });
        return;
      }
      void this.backend
        .refreshCalls()
        .then((view) => this.onActiveCallsChanged(view))
        .catch(() => undefined);
    }, RING_CHECK_MS);
  }

  private ringStillWanted(): boolean {
    const incoming = this.state.incoming;
    if (!incoming) return false;
    const call =
      this.activeView.calls.find((c) => (incoming.callId ? c.id === incoming.callId : c.channelId === incoming.channelId)) ?? null;
    return ringKeepsGoing({ call, currentUserId: this.me, callerId: incoming.callerId, ringingMs: Date.now() - this.ringStartedAt });
  }

  private stopRinging(clearAlert = true): void {
    if (clearAlert) this.clearIncomingAlert();
    if (this.ringCheck) clearInterval(this.ringCheck);
    this.ringCheck = null;
    this.ringer.stop();
  }

  /**
   * IncomingCallAlert: the ring is only HEARD, and a tab is usually behind other windows — so a
   * call also raises a notification that reads as a call, silent (the loop is the sound), taken
   * down the instant the ring stops. Only while the page is not in front (in front, the card is
   * the alert), and only with the permission the notifications feature already asked for.
   */
  private raiseIncomingAlert(callerName: string, callerId: string): void {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible' && document.hasFocus()) return;
    try {
      const note = new Notification(callerName || t('call.incoming.title'), {
        body: callerName ? tf('call.incoming.from', callerName) : t('call.incoming.title'),
        icon: callerId ? this.backend.avatarUrl(callerId) : undefined,
        tag: INCOMING_TAG,
        requireInteraction: true,
        silent: true,
      } as NotificationOptions);
      note.onclick = () => {
        window.focus();
        note.close();
      };
      this.incomingNotification = note;
    } catch (error) {
      console.warn('[satori] incoming call notification', error);
    }
  }

  private clearIncomingAlert(): void {
    this.incomingNotification?.close();
    this.incomingNotification = null;
  }

  /** The ringback while this device is ALONE on a connected call; never again once a peer was here. */
  private updateWaitingTone(): void {
    if (Object.keys(this.roster).length > 1) this.sawPeerThisCall = true;
    const alone = this.state.phase === 'active' && Object.keys(this.roster).length <= 1 && !this.sawPeerThisCall;
    if (alone && !this.waitingTone.playing) this.waitingTone.start('ringback');
    else if (!alone) this.waitingTone.stop();
  }

  private publishRoster(): void {
    const participants = Object.values(this.roster).sort((a, b) => (a.sessionId < b.sessionId ? -1 : a.sessionId > b.sessionId ? 1 : 0));
    const people = { ...this.activeView.people, ...this.state.people };
    const missing = [...new Set(participants.map((p) => p.userId).filter((id) => id && !people[id]))];
    if (missing.length) {
      void this.backend
        .callPeople(missing)
        .then((found) => {
          if (Object.keys(found).length) this.set({ people: { ...this.state.people, ...found } });
        })
        .catch(() => undefined);
    }
    this.set({
      participants,
      people,
      remoteScreenSharing: !!this.state.remoteScreen || participants.some((p) => p.sharingScreen && p.sessionId !== this.state.sessionId),
    });
    this.updateWaitingTone();
  }

  // MARK: - Socket events

  private onSocketJoined(): void {
    if (this.rtc) return;
    const own = this.socket?.sessionId;
    if (own) this.set({ sessionId: own });
    const rtc = new CallRtcSession(this.iceServers, {
      localSdp: (json) => this.socket?.sendSdp(json),
      localCandidate: (json) => this.socket?.sendIce(json),
      connected: () => {
        if (this.state.phase === 'joining') {
          this.clearJoinWatchdog();
          this.set({ phase: 'active' });
          this.updateWaitingTone();
        }
      },
      remoteScreen: (stream) => {
        if (stream === this.state.remoteScreen) return;
        // The Mac's huddle window opens by itself when somebody starts sharing.
        this.set({ remoteScreen: stream, remoteScreenSharing: !!stream, expanded: stream && !this.state.remoteScreen ? true : this.state.expanded });
      },
      remoteVideos: (bySession) => {
        const before = this.state.remoteVideos;
        const changed = Object.keys(bySession).length !== Object.keys(before).length || Object.entries(bySession).some(([k, v]) => before[k] !== v);
        if (changed) this.set({ remoteVideos: bySession });
      },
      remoteAudio: (track) => this.playRemoteAudio(track),
      connectionState: (state) => this.logCallEvent(`rtc state=${state}`),
      failed: (reason) => {
        this.logCallEvent(`rtc failed reason=${reason}`);
        if (this.state.phase !== 'idle') this.teardown('callFailed');
      },
    });
    this.rtc = rtc;
    rtc.start();
  }

  /** Remote voices play through hidden <audio> elements (a page has no audio unit of its own). */
  private playRemoteAudio(track: MediaStreamTrack): void {
    if (this.audioElements.has(track.id)) return;
    const audio = document.createElement('audio');
    audio.autoplay = true;
    audio.dataset.callAudio = track.id;
    audio.style.display = 'none';
    audio.srcObject = new MediaStream([track]);
    document.body.appendChild(audio);
    this.applyAudioOutput(audio);
    void audio.play().catch((error) => this.logCallEvent(`audio play blocked: ${String(error)}`));
    this.audioElements.set(track.id, audio);
    track.addEventListener('ended', () => {
      audio.srcObject = null;
      audio.remove();
      this.audioElements.delete(track.id);
    });
  }

  private onCallState(callJson: string): void {
    const parsed = CallRoster.fromCallStateJson(callJson);
    // An UNPARSEABLE snapshot must not wipe a roster that events built.
    if (!Object.keys(parsed).length && Object.keys(this.roster).length) {
      this.logCallEvent(`call_state parsed EMPTY (kept roster=${Object.keys(this.roster).length})`);
    } else {
      this.roster = parsed;
    }
    const own = this.state.sessionId ? this.roster[this.state.sessionId] : undefined;
    if (own) this.set({ handRaised: handRaised(own) });
    this.publishRoster();
  }

  private onRosterEvent(event: CallSocketEvent): void {
    if (event.kind === 'callEnd') {
      this.teardown('callEnded');
      return;
    }
    if (!this.state.sessionId && this.socket?.sessionId) this.set({ sessionId: this.socket.sessionId });
    if (event.kind === 'reacted') {
      if (event.sessionId !== this.state.sessionId || !this.state.sessionId) this.addReaction(event.userId, event.sessionId, CallEmoji.glyph(event.emoji));
      return;
    }
    if (event.kind === 'handState' && event.sessionId === this.state.sessionId) this.set({ handRaised: event.raisedAt > 0 });
    this.chimeIfNeeded(event);
    this.roster = CallRoster.apply(this.roster, event);
    this.publishRoster();
  }

  // MARK: - Screen awake (CallScreenPolicy through the Screen Wake Lock)

  private applyScreenPolicy(): void {
    // One writer for the page's wake lock (ui/screenAwake.ts, PARITY P304): the call's KEY, so a
    // voice note or the remote-support viewer ending never drops the call's hold.
    screenAwake.set(ScreenAwake.call, CallScreenPolicy.decide(this.state.phase, 'speaker').keepScreenAwake);
  }

}

/** `HTMLMediaElement.setSinkId` exists (Chromium, Edge/WebView2, Firefox 116+; not Safari). */
export function canRouteAudio(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}
